import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { CodeXml, Loader2 } from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

import functionsService from "../../services/functionsService";

import { isEmailValid } from "../../helpers/emailHelper";
import { useToast } from "../../components/hooks/use-toast";

const ResetPassword = () => {
  const [email, setEmail] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (isEmailValid(email)) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [email]);

  const sendPasswordResetEmail = async () => {
    setIsSendingEmail(true);

    try {
      const response = await functionsService.resetUserPassword(email);

      if (response.success) {
        toast({
          title: "Reset link sent",
          description:
            "Please check your email for password reset instructions.",
        });

        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        throw new Error("Password reset failed");
      }
    } catch (error) {
      console.error("Password reset error:", error);

      toast({
        variant: "destructive",
        title: "Password Reset Failed",
        description:
          error.message || "An error occurred while resetting the password.",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <a href="/" className="flex items-center gap-2 self-center font-medium">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <CodeXml className="size-4" />
          </div>
          Allayze
        </a>

        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-xl">Reset Password</CardTitle>

              <CardDescription>
                Enter your email address and we'll send you instructions to
                reset your password.
              </CardDescription>
            </CardHeader>

            <CardContent>
              <div className="grid gap-6">
                <div className="grid gap-6">
                  <div className="grid gap-2">
                    <Label htmlFor="email">Email</Label>

                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="abc@example.com"
                      disabled={isSendingEmail}
                      required
                    />
                  </div>

                  <Button
                    className="w-full"
                    disabled={!isValid || isSendingEmail}
                    onClick={sendPasswordResetEmail}
                  >
                    {isSendingEmail ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <span>Send email</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
